/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import moment from 'moment'
import qs from 'query-string'

import {
  FilterSidebar,
  Identities,
  FilterComboEmployerDivision,
  FilterEmploymentType,
  FilterEmploymentStatus,
  FilterMembershipStatus,
  FilterProductsStatus,
  FilterDateRange,
  FilterPicklist
} from '../'

const defaultState = {
  search: '',
  divisionFilter: '',
  employmentTypeFilter: '',
  employmentStatusFilter: 'active',
  membershipTypeFilter: '',
  membershipStatusFilter: '',
  productsStatusFilter: '',
  createdAtStartDate: null,
  createdAtEndDate: null,
  identityTypeFilter: 'Client',
  orderBy: 'first_name'
}

class EmployerEmployees extends React.PureComponent {
  static propTypes = {
    employerId: PropTypes.string.isRequired
  }

  state = defaultState

  UNSAFE_componentWillMount() {
    if (this.props.location.search) {
      // if initially url was entered, or copy-and-pasted, with filters specified in url search params, set state accordingly
      this.setFiltersFromQueryString()
    }
     else {
      // if initially, page was opened via global nav, set default url search params according to current initial state filter variables
      this.setQueryString(true)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    let urlSearchParamsChanged = prevProps.location.search !== this.props.location.search
    let urlSearchParamsNowEmpty = prevProps.location.search !== '' && this.props.location.search === ''

    if (urlSearchParamsNowEmpty) {
      // if search params empty because user clicked on global nav, set state filter variables and url to default
      this.setState(defaultState, () => this.setQueryString())
    } else if (urlSearchParamsChanged) {
      // if url search params have changed, update state
      this.setFiltersFromQueryString()
    }
  }

  setFiltersFromQueryString = () => {
    let queryParams = qs.parse(this.props.location.search)

    let filters = Object.assign(
      {},
      ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
        ['search', 'orderBy'].indexOf(key) > -1
         ? { [key]: queryParams[key] }
         : [ 'createdAtStartDate', 'createdAtEndDate' ].indexOf(key) > -1  // end/startDate query params aren't translated into state variables with -'Filter' appended like the others
           ? { [ key ]: moment(queryParams[key], 'YYYYMMDDTHHmm') }
           : { [`${key}Filter`]: queryParams[key] }
      ))
    )

    this.setState(filters)
  }

  setQueryString = (setDefault) => {
    let state = this.state
    let filters = {
      orderBy: state.orderBy
    }

    if (state.search !== '') {
      filters.search = state.search
    }

    if (state.divisionFilter !== '') {
      filters.division = state.divisionFilter
    }

    if (setDefault) {
      filters.identityType = 'Client'
    }
    else if (state.identityTypeFilter !== '') {
      filters.identityType = state.identityTypeFilter
    }

    if (state.employmentTypeFilter !== '') {
      filters.employmentType = state.employmentTypeFilter
    }

    if (setDefault && state.identityTypeFilter === 'Client') {
      filters.employmentStatus = 'active'
    } else if (state.employmentStatusFilter !== '') {
      filters.employmentStatus = state.employmentStatusFilter
    }

    if (state.membershipTypeFilter !== '') {
      filters.membershipType = state.membershipTypeFilter
    }

    if (state.membershipStatusFilter !== '') {
      filters.membershipStatus = state.membershipStatusFilter
    }

    if (state.productsStatusFilter !== '') {
      filters.productsStatus = state.productsStatusFilter
    }

    if (state.createdAtStartDate) {
      filters.createdAtStartDate = state.createdAtStartDate.format('YYYYMMDDTHHmm')
    }
    if (state.createdAtEndDate) {
      filters.createdAtEndDate = state.createdAtEndDate.format('YYYYMMDDTHHmm')
    }

    if (Object.keys(filters).length > 0) {
      this.props.history.push({
        search: qs.stringify(filters)
      })
    }
  }

  getFilters = () => (
    this.state.identityTypeFilter === 'Client'
      ? this.getClientFilters()
      : this.getContactFilters()
  )

  getClientFilters = () => (
    [
      <FilterComboEmployerDivision
        employer={this.props.employerId}
        division={this.state.divisionFilter}
        onEmployerChange={() => false}
        onDivisionChange={division => this.setState(
          {
            divisionFilter: division === 'All'
              ? ''
              : division
          }, () => this.setQueryString()
        )}
        disableEmployerChoice={true}
        key='employerDivisionComboFilter'
      />,
      <FilterPicklist
        options={['Client', 'Contact']}
        value={this.state.identityTypeFilter}
        onChange={identityType => (
          this.setState({
            identityTypeFilter: identityType,
            employmentStatusFilter: identityType === 'Client'
              ? 'active'
              : ''
          },
          () => this.setQueryString())
        )}
        key='client_identityTypeFilter'
      />,
      <FilterEmploymentType
        value={this.state.employmentTypeFilter}
        onChange={employmentType => this.setState(
          {
            employmentTypeFilter: employmentType === 'all'
              ? ''
              : employmentType
          }, () => this.setQueryString()
        )}
        key='employmentTypeFilter'
      />,
      <FilterEmploymentStatus
        value={this.state.employmentStatusFilter}
        onChange={employmentStatus => this.setState(
          {
            employmentStatusFilter: employmentStatus === 'all'
              ? ''
              : employmentStatus
          }, () => this.setQueryString()
        )}
        key='employmentStatusFilter'
      />,
      <FilterMembershipStatus
        value={this.state.membershipStatusFilter}
        onChange={membershipStatus => this.setState(
          {
            membershipStatusFilter: membershipStatus === 'all'
              ? ''
              : membershipStatus
          }, () => this.setQueryString()
        )}
        key='membershipStatusFilter'
      />,
      <FilterProductsStatus
        value={this.state.productsStatusFilter}
        onChange={productsStatus => this.setState(
          {
            productsStatusFilter: productsStatus === 'all'
              ? ''
              : productsStatus
          }, () => this.setQueryString()
        )}
        key='productsStatusFilter'
      />,
      <FilterDateRange
        title='Created between'
        startDate={this.state.createdAtStartDate}
        endDate={this.state.createdAtEndDate}
        onStartDateChange={date => this.setState({ createdAtStartDate: date }, () => this.setQueryString())}
        onEndDateChange={date => this.setState({ createdAtEndDate: date }, () => this.setQueryString())}
        showTimeSelect={true}
        showYearDropdown={true}
        showMonthDropdown={true}
        key='createdBetweenFilter'
      />
    ]
  )

  getContactFilters = () => (
    [
      <FilterComboEmployerDivision
        employer={this.props.employerId}
        division={this.state.divisionFilter}
        onEmployerChange={() => false}
        onDivisionChange={division => this.setState(
          {
            divisionFilter: division === 'All' ? '' : division
          }, () => this.setQueryString()
        )}
        disableEmployerChoice={true}
        key='employerDivisionComboFilter'
      />,
      <FilterPicklist
        options={['Client', 'Contact']}
        value={this.state.identityTypeFilter}
        onChange={identityType => (
          this.setState({
            identityTypeFilter: identityType,
            employmentStatusFilter: identityType === 'Client'
              ? 'active'
              : ''
          },
          () => this.setQueryString())
        )}
        key='client_identityTypeFilter'
      />,
      <FilterMembershipStatus
        value={this.state.membershipStatusFilter}
        onChange={membershipStatus => this.setState(
          {
            membershipStatusFilter: membershipStatus === 'all' ? '' : membershipStatus
          }, () => this.setQueryString()
        )}
        key='membershipStatusFilter'
      />,
      <FilterDateRange
        title='Created between'
        startDate={this.state.createdAtStartDate}
        endDate={this.state.createdAtEndDate}
        onStartDateChange={date => this.setState({ createdAtStartDate: date }, () => this.setQueryString())}
        onEndDateChange={date => this.setState({ createdAtEndDate: date }, () => this.setQueryString())}
        showTimeSelect={true}
        showYearDropdown={true}
        showMonthDropdown={true}
        key='createdBetweenFilter'
      />
    ]
  )

  render() {
    let filters = this.getFilters()

    return (
      <div className='slds-grow slds-scrollable_y' role='main'>
        <div className='slds-grid slds-wrap slds-size_1-of-1 slds-clearfix'>
          <FilterSidebar
            filters={filters}
            componentFilters={true}
            filtersApplied={[
                this.state.divisionFilter,
                this.state.identityTypeFilter,
                this.state.employmentTypeFilter,
                this.state.employmentStatusFilter,
                this.state.membershipTypeFilter,
                this.state.membershipStatusFilter,
                this.state.productsStatusFilter,
                this.state.createdAtStartDate || this.state.createdAtEndDate
                  ? this.state.createdAtStartDate
                    ? this.state.createdAtStartDate.toString()
                    : this.state.createdAtEndDate.toString()
                  : ''
              ].filter(filter => filter.length > 0).length
            }
            onClear={() => this.setState({
              divisionFilter: '',
              identityTypeFilter: 'Client',
              employmentTypeFilter: '',
              employmentStatusFilter: '',
              membershipTypeFilter: '',
              membershipStatusFilter: '',
              productsStatusFilter: '',
              createdAtStartDate: null,
              createdAtEndDate: null
            }, () => this.setQueryString())}
            search={true}
            searchString={this.state.search}
            onSearchClick={search => this.setState({ search })}
            onSearchClear={() => this.setState({ search: '' })}
            key={this.state.identityTypeFilter}
          />
          <div className='slds-col slds-size_10-of-12 slds-p-around_small'>
            <Identities
              search={this.state.search
                ? this.state.search
                : ''
              }
              employer={this.props.employerId}
              division={this.state.divisionFilter}
              employmentType={this.state.employmentTypeFilter}
              employmentStatus={this.state.employmentStatusFilter}
              membershipType={this.state.membershipTypeFilter}
              membershipStatus={this.state.membershipStatusFilter}
              productsStatus={this.state.productsStatusFilter}
              createdAtStartDate={this.state.createdAtStartDate
                ? moment.utc(this.state.createdAtStartDate).toISOString()
                : null
              }
              createdAtEndDate={this.state.createdAtEndDate
                ? moment.utc(this.state.createdAtEndDate).toISOString()
                : null
              }
              identityType={this.state.identityTypeFilter !== ''
                ? this.state.identityTypeFilter.toLowerCase()
                : null
              }
              orderBy={this.state.orderBy}
              changeSort={orderBy => this.setState({ orderBy }, () => this.setQueryString())}
              key={this.state.identityFilter}
            />
          </div>
        </div>
      </div>
    )
  }
}

export default withRouter(EmployerEmployees)



// WEBPACK FOOTER //
// ./src/components/employer-employees/index.js